"use client"

import {Price} from "@/common/types";
import clsx from "clsx";
import {CheckCircleIcon} from "@heroicons/react/20/solid";
import {Button} from "@/components/button";
import React from "react";
import ServerRack1 from "@/components/server-rack-1"
import ServerRack2 from "@/components/server-rack-2"
import ServerRack3 from "@/components/server-rack-3"

const icons = [ServerRack1, ServerRack2, ServerRack3]

interface PlanProps {
  index: number
  name: string
  frequency: string
  prices: Price[]
  description: string
  href: string
  features: Array<string>
  featured?: boolean
  free_domain: boolean
}

export default function PricingPlan({index, name, frequency, prices, description, href, features, free_domain, featured = false}: Readonly<PlanProps>) {
  const preDiscountPrice = frequency === "year" && prices.find((p) => p.recurring_interval === "year")?.pre_discount_unit_amount
  const price = prices.find((p) => p.recurring_interval === frequency)?.unit_amount

  return (
    <section className={clsx('flex flex-col rounded-3xl px-6 sm:px-8', featured ? 'order-first bg-blue-600 py-8 lg:order-none' : 'lg:py-8',)}>
      {preDiscountPrice && <div className="text-5xl line-through order-first text-gray-100">${((preDiscountPrice! / 100) / 12).toFixed(2)}</div>}

      <div className={clsx("flex items-end order-first font-display font-light tracking-tight text-white", !preDiscountPrice && "order-first")}>
        <div className="text-5xl">{frequency == "month" ? `$${(price! / 100).toFixed(2)}` : `$${((price! / 100) / 12).toFixed(2)}`}</div>
        <div className="ml-2 pb-2">per month</div>
      </div>

      <p className="text-gray-300">{frequency == "month" ? 'billed monthly' : `billed yearly as $${(price! / 100).toFixed(2)}`}</p>

      <h3 className="flex mt-5 font-display text-lg text-white">
        {icons[index] && icons[index]({className: "w-6 h-6 fill-white"})}
        <span className="ml-4">{name}</span>
      </h3>
      <p className={clsx('mt-2 text-base', featured ? 'text-white' : 'text-slate-400')}>
        {description}
      </p>

      <ul className={clsx('order-last mt-10 flex flex-col gap-y-3 text-sm', featured ? 'text-white' : 'text-slate-200')}>
        {free_domain && (
          <li className="flex">
            <CheckCircleIcon width={20} height={20} className={featured ? 'text-white' : 'text-slate-400'}/>
            <span className="ml-4">Free domain</span>
          </li>
        )}
        {features.map((feature) => (
          <li key={feature} className="flex">
            <CheckCircleIcon width={20} height={20} className={featured ? 'text-white' : 'text-slate-400'}/>
            <span className="ml-4">{feature}</span>
          </li>
        ))}
      </ul>
      <Button
        href={href}
        variant={featured ? 'solid' : 'outline'}
        color="white"
        className="mt-8"
        aria-label={`Get started with the ${name} plan for ${(price! / 100).toFixed(2)}`}
      >
        {`Get Your Server Now`}
      </Button>
    </section>
  )
}
